import React, { useCallback, useEffect, useRef, useState } from 'react';
import VideoUploader from '../../../components/VideoUploader/VideoUploader';
import { MdImage, MdVideocam, MdLock, MdCloudUpload } from 'react-icons/md';
import DraftsBig from '../../../components/Drafts/DraftsBig';
import { getFileTypeFromFirebasePath } from '../../../utils/getFileType';
import { useLocation } from 'react-router-dom';
import UsageLimits from '../Tools/UsageLimits';
import { useQuota } from '../../../context/QuotaContext';

const Upload = ({ isModal = false, draftSchedule, showFeeds = false }) => {
    const [selectedOption, setSelectedOption] = useState(null);
    const hasRunOnceRef = useRef(false);
    const hasRunOnceRef2 = useRef(false);
    const location = useLocation();
    const [draft, setDraft] = useState(null);
    
    // Use QuotaContext instead of local state
    const { quota, loading: quotaLoading, refreshQuota, fetchQuota } = useQuota();
    
    // Calculate upload limit state from quota context
    const isUploadLimitReached = quota?.upload ? 
        !quota.upload.unlimited && quota.upload.used >= quota.upload.limit : 
        false;

    // Fetch upload quota on component mount
    useEffect(() => {
        if (!quotaLoading && !quota?.upload) {
            fetchQuota({ showUpload: true, showTwitter: true });
        }
    }, [fetchQuota, quota?.upload, quotaLoading]);

    const handleOptionSelect = (option) => {
        // Don't allow selection if quota is still loading
        if (quotaLoading) {
            return;
        }
        
        // Don't allow selection if upload limit is reached
        if (isUploadLimitReached) {
            return;
        }
        setSelectedOption(option);
    };

    // Memoize handleEditDraft to prevent unnecessary re-renders
    const handleEditDraft = useCallback((draft) => {
        // Don't allow editing if quota is still loading
        if (quotaLoading) {
            return;
        }
        
        // Don't allow editing if upload limit is reached
        if (isUploadLimitReached) {
            return;
        }

        let draftData = {};
        if (draft && draft.posts && draft?.posts[0] !== undefined) {
            draftData = draft;
        } else {
            draftData.posts = [draft];
        }
        setDraft(draftData);

        if (draftData.posts[0].carousel_images && draftData.posts[0].carousel_images.length > 0) {
            setSelectedOption('carousel');
        } else if (getFileTypeFromFirebasePath(draftData.posts[0].firebase_path) === 'video') {
            setSelectedOption('video');
        } else {
            setSelectedOption('post');
        }
    }, [quotaLoading, isUploadLimitReached]);

    // Handle draft schedule editing
    useEffect(() => {
        if (!hasRunOnceRef2.current && draftSchedule) {
            handleEditDraft(draftSchedule);
            hasRunOnceRef2.current = true;
        }
    }, [draftSchedule, handleEditDraft]);

    // Handle location state editing
    useEffect(() => {
        if (!hasRunOnceRef.current && location.state?.upload) {
            handleEditDraft(location.state.upload);
            hasRunOnceRef.current = true;
        }
    }, [location.state?.upload, handleEditDraft]);

    if (selectedOption) {
        return <VideoUploader 
            uploadType={selectedOption} 
            draft={draft} 
            onQuotaRefresh={refreshQuota}
            isUploadLimitReached={isUploadLimitReached}
            quotaLoading={quotaLoading}
        />;
    }

    const isButtonsDisabled = quotaLoading || isUploadLimitReached;

    return (
        <div className={`dashboard-container flex flex-col gap-3 h-full overflow-y-auto ${isModal ? 'h-[80px]' : ''}`}>
            {/* Usage Limits Display */}
            <div className="flex justify-end mb-4 mr-0 md:mr-10">
                <div className="flex flex-wrap gap-2">
                    <UsageLimits 
                        showGpt={false}
                        showImage={false}
                        showUpload={true}
                        showTwitter={true}
                        variant="pill"
                    />
                </div>
            </div>
            
            {/* Descriptive Text and Buttons - 60% height on desktop */}
            <div className='md:h-[80%] h-full flex flex-col items-center justify-center'>
                <h2 className="text-2xl font-medium text-center py-5">
                    What content do you want to upload?
                </h2>

                {/* Status Message */}
                {quotaLoading && (
                    <div className="mb-4 flex items-center gap-2 text-gray-600">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        <span className="text-sm">Checking your upload limits...</span>
                    </div>
                )}
                
                {isUploadLimitReached && !quotaLoading && (
                    <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800 max-w-md">
                        <MdLock size={20} />
                        <div>
                            <p className="font-medium text-sm">Upload limit reached</p>
                            <p className="text-xs text-amber-700">Upgrade your plan to continue uploading content</p>
                        </div>
                    </div>
                )}

                {/* Primary Buttons Container */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-5">
                    <div
                        className={`w-[300px] h-[200px] border-2 font-semibold text-lg rounded-xl transition-all duration-200 flex flex-col items-center justify-center cursor-pointer relative overflow-hidden ${
                            isButtonsDisabled 
                                ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50' 
                                : 'border-border hover:border-blue-200 hover:shadow-md hover:bg-blue-50/30'
                        }`}
                        onClick={() => handleOptionSelect('post')}
                    >
                        {quotaLoading && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                <div className="animate-pulse flex flex-col items-center">
                                    <div className="w-9 h-9 bg-gray-300 rounded mb-2"></div>
                                    <div className="w-20 h-4 bg-gray-300 rounded"></div>
                                </div>
                            </div>
                        )}
                        <MdImage className="mb-2" size={36} />
                        Post/Image
                    </div>
                    
                    <span className={`text-gray-500 ${isButtonsDisabled ? 'text-gray-400' : ''}`}>or</span>
                    
                    <div
                        className={`w-[300px] h-[200px] border-2 font-semibold text-lg rounded-xl transition-all duration-200 flex flex-col items-center justify-center cursor-pointer relative overflow-hidden ${
                            isButtonsDisabled 
                                ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50' 
                                : 'border-border hover:border-blue-200 hover:shadow-md hover:bg-blue-50/30'
                        }`}
                        onClick={() => handleOptionSelect('video')}
                    >
                        {quotaLoading && (
                            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                <div className="animate-pulse flex flex-col items-center">
                                    <div className="w-9 h-9 bg-gray-300 rounded mb-2"></div>
                                    <div className="w-16 h-4 bg-gray-300 rounded"></div>
                                </div>
                            </div>
                        )}
                        <MdVideocam className="mb-2" size={36} />
                        Video
                    </div>
                </div>

                {/* Secondary Options - Carousel and Bulk Upload */}
                <div className="mt-8 flex justify-center">
                    <div className="flex flex-col md:flex-row items-center justify-center gap-5">
                        {/* Carousel Option */}
                        <div className="text-center">
                            <div
                                className={`w-[250px] h-[100px] border-2 font-semibold text-sm rounded-lg transition-all duration-200 flex flex-col items-center justify-center cursor-pointer relative overflow-hidden ${
                                    isButtonsDisabled 
                                        ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50' 
                                        : 'border-border hover:border-purple-200 hover:shadow-md hover:bg-purple-50/30'
                                }`}
                                onClick={() => handleOptionSelect('carousel')}
                            >
                                {quotaLoading && (
                                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                        <div className="animate-pulse flex flex-col items-center">
                                            <div className="w-6 h-6 bg-gray-300 rounded mb-2"></div>
                                            <div className="w-16 h-3 bg-gray-300 rounded"></div>
                                        </div>
                                    </div>
                                )}
                                <MdImage className="mb-2" size={24} />
                                <span className="font-medium">Carousel</span>
                                <p className="text-gray-500 text-[10px]">For Instagram and TikTok</p>
                            </div>
                        </div>

                        {/* Bulk Upload Option */}
                        <div className="text-center">
                            <div
                                className={`w-[250px] h-[100px] border-2 font-semibold text-sm rounded-lg transition-all duration-200 flex flex-col items-center justify-center cursor-pointer relative overflow-hidden ${
                                    isButtonsDisabled 
                                        ? 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-50' 
                                        : 'border-border hover:border-blue-200 hover:shadow-md hover:bg-blue-50/30'
                                }`}
                                onClick={() => handleOptionSelect('bulk')}
                            >
                                {quotaLoading && (
                                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                                        <div className="animate-pulse flex flex-col items-center">
                                            <div className="w-6 h-6 bg-gray-300 rounded mb-2"></div>
                                            <div className="w-16 h-3 bg-gray-300 rounded"></div>
                                        </div>
                                    </div>
                                )}
                                <MdCloudUpload className="mb-2" size={24} />
                                <span className="font-medium">Bulk Upload</span>
                                <p className="text-gray-500 text-[10px]">Multiple files at once</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Drafts Component - 40% height on desktop, full height on mobile (hidden by default on mobile) */}
            <div className="hidden md:block md:!h-[50%] rounded-md w-full px-[2.5%]">
                <DraftsBig onEditDraft={handleEditDraft} itemsPerPage={5} showFeeds={showFeeds} initialShowFeeds={false} />
            </div>
            
            {/* Drafts Component - Only visible on mobile and takes full height */}
            <div className="md:hidden h-full rounded-md w-full mt-8 px-[2.5%]">
                <DraftsBig onEditDraft={handleEditDraft} itemsPerPage={5} showFeeds={showFeeds} initialShowFeeds={false} />
            </div>
        </div>
    );
};
export default Upload;
